import { Router } from '@angular/router';
import { AUTH_STORAGE_KEY } from '@knowledgehook/auth-storage';
import { GameModes } from '@nova/types';

import { GameDisallowedReason } from '../../constants/game-disallowed-reason';
import { environment } from 'env/environment';

export enum GameConnectionType {
  SP = 'SP',
  MP = 'MP'
}
export interface GameUrlParams {
  classId: string;
  game: GameModes;
  ct?: GameConnectionType; // single player or multiplayer
  lobbyId?: string;
}

type LockGameFn = (reason: GameDisallowedReason | undefined) => void;

/**
 * A game mode that can be played.
 */
export class Game {
  private _id: string;
  private _gameMode: string;
  private _icon: string;
  private _name: string;
  private _splash: string;
  private _thumb: string;
  private _description: string;
  private _locked: boolean | undefined;
  private _lockReason: GameDisallowedReason | undefined;
  private _lockGameFn: LockGameFn | undefined;
  private _hasTutorial: boolean | undefined;
  private _tutorialCompleted: boolean | undefined;
  private _multiplayerCapable = false;
  private _multiplayerEnabled = false;
  private _playAfterGameShowUntil: string | null = null;

  private _gameUrlParams: GameUrlParams | undefined;
  private _route: string[] | undefined;

  public constructor(rawGame: any) {
    this._id = rawGame.id;
    this._gameMode = rawGame.gameMode;
    this._icon = rawGame.icon;
    this._name = rawGame.name;
    this._splash = rawGame.splash;
    this._thumb = rawGame.thumb;
    this._description = rawGame.description;
    this._locked = rawGame.locked;
    this.lockReason = rawGame.lockReason;
    this._hasTutorial = rawGame.hasTutorial;
    this._tutorialCompleted = rawGame.tutorialCompleted;
    this._multiplayerCapable = rawGame.multiplayerCapable;
    this._multiplayerEnabled = rawGame.multiplayerEnabled;
    this._playAfterGameShowUntil = rawGame.playAfterGameShowUntil ?? null;
  }

  public get id(): string {
    return this._id;
  }

  public get gameMode(): string {
    return this._gameMode;
  }

  public name(s: string) {
    if (!s || s.length < 1) { throw new TypeError(`Game name must be a non-empty string, but it was "${s}"`); }
  }

  public get multiplayerEnabled(): boolean {
    return this._multiplayerEnabled;
  }

  /**
     * @param mode - A description of the play mode. i.e. 1 Player,  5 Player co-op etc.
     * @throws - When the mode is an empty string.
     */
  public set description(description: string | undefined) {
    if (!description) { throw new Error('description cannot be an empty string or undefined.'); }
    this._description = description;
  }

  public get description(): string {
    return this._description;
  }

  public get splashImageURL(): string {
    return this._splash;
  }

  public get thumbImageURL(): string {
    return this._thumb;
  }

  /**
     * @returns The name of the game.
     */
  public getName(): string {
    return this._name;
  }

  public getIconURL(): string {
    return this._icon;
  }

  public get locked(): boolean | undefined {
    return this._locked;
  }

  public set lockReason(reason: GameDisallowedReason | undefined) {
    this._lockReason = reason;
  }

  public get lockReason(): GameDisallowedReason | undefined {
    return this._lockReason;
  }

  /**
     * Overides the servers lock state to make the game locked, with the given reason.
     * To be used in cases where something went weary on the frontend and the game needs to be
     * un-selectable because of it.
     *
     * @param reason - The reason for locking.
     */
  public lock(reason: string) {
    this._locked = true;
    this._lockReason = Object.values(GameDisallowedReason).find((f) => f === reason);
    this._description = reason;
  }

  /**
     * @param fn - The function to execute when the user attempts to start the locked game.
     * This will usually be a function which opens a modal, or a routing function.
     */
  public setLockGameFn(fn: LockGameFn): void {
    this._lockGameFn = fn;
  }

  /**
     * Attempts to start the game if its not locked.
     *
     * When running locally (environment.local = true), this method will:
     * 1. Check if a local development version of the game is available
     * 2. If available, redirect to the game at the local URL
     * 3. If not available, fall back to the default Angular routing behavior
     */
  public async start(router: Router): Promise<void> {
    if (!this.locked) {
      if (!this._gameUrlParams) { throw new Error(`gameUrlParams for game "${this._id}" has not be set.`); }

      if (!this._route) { throw new Error(`route for game "${this._id}" has not be set.`); }

      if (environment.local && environment.localGameBaseUrl) {
        // Append the gameUrlParams to the localGameUrl as query parameters
        const localGameUrl = (() => {
          const baseUrl = environment.localGameBaseUrl;
          const params = this._gameUrlParams
            ? new URLSearchParams(Object.entries(this._gameUrlParams).map(([k, v]) => [k, String(v)])).toString()
            : '';
          return params ? `${baseUrl}?${params}` : baseUrl;
        })();

        // Redirect to the game URL with localStorage data as URL parameters
        const gameUrlWithData = this.buildGameUrlWithLocalStorage(localGameUrl);
        window.location.assign(gameUrlWithData);

        return;
      }

      // Default behavior: navigate within the Angular app
      await router.navigate(this._route, { queryParams: this._gameUrlParams });
    }
    else {
      if (!this._lockGameFn) { throw new Error('Locked game function for game has not been set.'); }
      this._lockGameFn(this._lockReason);
    }
  }

  public setRoute(route: string[]) {
    this._route = route;
  }

  public setUrlParams(urlParams: GameUrlParams) {
    this._gameUrlParams = urlParams;
  }

  public addConnectTypeToUrlParams(ct: GameConnectionType) {
    this._gameUrlParams && (this._gameUrlParams.ct = ct);
  }

  public addLobbyIdToUrlParams(lobbyId: string) {
    this._gameUrlParams && (this._gameUrlParams.lobbyId = lobbyId);
  }

  public toString(): string {
    return JSON.stringify(this);
  }

  public get hasTutorial(): boolean | undefined {
    return this._hasTutorial;
  }

  public get tutorialCompleted(): boolean | undefined {
    return this._tutorialCompleted;
  }

  public get multiplayerCapable(): boolean {
    return this._multiplayerCapable;
  }

  public get isTutorialRequired(): boolean {
    if (this._gameMode === GameModes.OutpostRun) { return false; }
    return !!this._hasTutorial && !this._tutorialCompleted;
  }

  /**
   * ISO 8601 UTC datetime until which "Play After GameShow" is active for this game,
   * or `null` if the student is not currently inside a PAGS window.
   */
  public get playAfterGameShowUntil(): string | null {
    return this._playAfterGameShowUntil;
  }

  /**
   * Builds the game URL with localStorage data as URL parameters.
   * @param baseUrl - The base game URL
   * @returns URL with localStorage data as query parameters
   */
  private buildGameUrlWithLocalStorage(baseUrl: string): string {
    try {
      if (!environment.local) {
        return baseUrl; // ! - Only run if we're in local development mode
      }

      if (!environment.localGameBaseUrl) {
        return baseUrl; // ! - Only run if we have a local game base URL
      }

      const url = new URL(baseUrl);

      const gameLocalStorageKeys = ['CoreApiUrl', AUTH_STORAGE_KEY];

      gameLocalStorageKeys.forEach((key) => {
        const value = localStorage.getItem(key);
        if (value !== null) {
          // Skip very long values to avoid URL length limits
          if (value.length < 1000) {
            url.searchParams.set(`ls_${key}`, value);
          }
          else {
            console.warn(`[Game] Skipping localStorage key: ${key} because it's too long: ${value.length.toString()} characters`);
          }
        }
      });

      // Set GameApiUrl to override if set in local environment variables
      if (environment.overrideGameApiUrl) {
        url.searchParams.set('ls_overrideGameApiUrl', environment.overrideGameApiUrl);
      }

      return url.toString();
    }
    catch (e) {
      console.error('[Game] Error building URL with localStorage data:', e);
      return baseUrl;
    }
  }
}
